import React, { useState, useEffect } from 'react';
import { displayStaffName } from '../../lib/customerIdentity';
import { formatInTenantTz } from '../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { useWebRTCContext } from '../WebRTC/WebRTCContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { auth } from '../../lib/firebase';
import { useToast } from '../../hooks/use-toast';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import OnlineUsersPanel from '../WebRTC/OnlineUsersPanel';
import StaffChatModal from '../WebRTC/StaffChatModal';
import { 
  Phone, 
  PhoneCall, 
  Clock,
  User,
  MessageSquare,
  TrendingUp,
  AlertCircle,
  Video,
  Mic,
  PhoneOff,
  Users,
  BarChart3,
  Activity,
  Hash,
  UserPlus,
  History
} from 'lucide-react';

const VoiceCalls = () => {
  const { currentBusiness } = useBusiness();
  const { user } = useAuth();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { toast } = useToast();
  const { t } = useLanguage();

  // Call History RBAC/privacy: a call record is private staff data (who called whom). Supervisory
  // roles (manager/admin) may see the whole team's history; everyone else sees only their own.
  // The backend ENFORCES this scoping on /webrtc/call-logs + /call-stats; the label here just
  // reflects the scope honestly so users know whose calls they are looking at.
  const _callHistoryRole = (user?.role || '').toLowerCase();
  const isCallHistorySupervisor = _callHistoryRole === 'manager' || _callHistoryRole === 'admin';
  const callHistoryLabel = isCallHistorySupervisor ? t('tc.teamCallHistory') : t('tc.myCallHistory');

  // Customer interaction functions for Quick Actions
  const handleStartCustomerCall = async (callType) => {
    try {
      const callTypeText = callType === 'voice' ? 'Voice Call' : 'Video Call';

      // Ring a real ONLINE NON-SELF staff member (same source as Quick Call Contacts).
      // (Was onlineUsers[0] — a raw uid string that could be self; and passed a boolean as callType.)
      const target = realContacts[0];
      if (!target) {
        toast({
          title: t('tc.toast.noStaffOnline.title'),
          description: t('tc.toast.noStaffOnline.desc'),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: `Starting ${callTypeText}`,
        description: `Calling ${target.name}...`,
      });

      // initiateCall now returns a result (device-aware) instead of throwing for handled cases: a
      // video call with no camera proceeds as audio (ok:true), a stale/superseded attempt is suppressed,
      // and only a genuine failure returns ok:false with an actionable message.
      const res = await initiateCall(target.id, callType);
      if (res && res.ok === false && !res.suppressed) {
        toast({
          title: "Call Failed",
          description: res.message || `Could not start ${callType} call.`,
          variant: "destructive",
        });
      }

    } catch (error) {
      console.error('Call initiation error:', error?.code || 'unknown');
      toast({
        title: "Call Failed",
        description: error?.userMessage || error?.message || `Could not start ${callType} call.`,
        variant: "destructive",
      });
    }
  };

  const handleStartCustomerConversation = () => {
    try {
      // Open dialog to select customer for conversation using existing Tickets/Conversations system
      setQuickConversationOpen(true);
      
    } catch (error) {
      toast({
        title: "Conversation Failed",
        description: "Failed to open conversation dialog. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleInitiateQuickConversation = () => {
    if (!conversationContactName.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter contact name",
        variant: "destructive"
      });
      return;
    }

    // Store contact data for Tickets/Conversations system.
    // Only real, user-entered values are passed — we do NOT fabricate a placeholder
    // email or phone. Phone is included only if the operator actually typed one.
    sessionStorage.setItem('newConversationCustomer', JSON.stringify({
      id: `quick_${Date.now()}`,
      name: conversationContactName,
      phone: conversationContactPhone.trim(),
      email: '',
      company: ''
    }));

    toast({
      title: "Opening Conversation",
      description: `Starting chat with ${conversationContactName}...`,
    });

    // Navigate to Tickets/Conversations system
    setTimeout(() => {
      window.location.href = '/business/tickets';
    }, 500);

    setQuickConversationOpen(false);
    setConversationContactName('');
    setConversationContactPhone('');
  };
  const [callHistory, setCallHistory] = useState([]);
  const [callStats, setCallStats] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [statsRefreshing, setStatsRefreshing] = useState(false);   // header "Refresh Stats" in-flight guard
  const [lastStatsRefresh, setLastStatsRefresh] = useState(null);  // timestamp of last SUCCESSFUL refresh
  const [quickCallOpen, setQuickCallOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [teamConversationOpen, setTeamConversationOpen] = useState(false);
  const [staffChatPeer, setStaffChatPeer] = useState(null);   // staff-to-staff DM target
  const [staffUnread, setStaffUnread] = useState({});         // peerId -> unread count (badge on roster)
  const [rosterLoading, setRosterLoading] = useState(false);  // Team Members refresh visible state
  const [rosterError, setRosterError] = useState(false);      // roster fetch failed (403/500) — not "no staff"
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [conversationMessage, setConversationMessage] = useState('');
  const [quickConversationOpen, setQuickConversationOpen] = useState(false);
  const [conversationContactName, setConversationContactName] = useState('');
  const [conversationContactPhone, setConversationContactPhone] = useState('');
  const [staffRoster, setStaffRoster] = useState([]);

  // Ticket creation states
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  const [ticketData, setTicketData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });
  // M17 — customer support ticket modal (canonical customer + conversation; never team member)
  const [ticketCustomers, setTicketCustomers] = useState([]);
  const [ticketCustomersLoading, setTicketCustomersLoading] = useState(false);
  const [selTicketCustomerId, setSelTicketCustomerId] = useState('');
  const [ticketConversations, setTicketConversations] = useState([]);
  const [ticketConvLoading, setTicketConvLoading] = useState(false);
  const [selTicketConversationId, setSelTicketConversationId] = useState('');
  const [ticketIdemKey, setTicketIdemKey] = useState('');
  const [ticketSubmitting, setTicketSubmitting] = useState(false);

  const {
    isConnected,
    onlineUsers,
    currentCall,
    incomingCall,
    callStatus,
    localVideoRef,
    remoteVideoRef,
    remoteStream,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    fetchOnlineUsers,
    // staff audio conference (mesh)
    startConference,
    conference,
    confError
  } = useWebRTCContext();   // shared singleton — no second socket

  useEffect(() => {
    if (currentBusiness?.brandId) {
      fetchCallHistory();
      fetchCallStats();
      fetchStaffRoster();
    }
  }, [currentBusiness]);

  // Poll staff-chat unread while the page is mounted so incoming DMs surface as a roster badge.
  useEffect(() => {
    if (!currentBusiness?.brandId) return;
    fetchStaffConversations();
    const t = setInterval(fetchStaffConversations, 6000);
    return () => clearInterval(t);
  }, [currentBusiness?.brandId]);

  // Real tenant staff roster — used to resolve live-presence ids (onlineUsers) to names/roles.
  const fetchStaffRoster = async () => {
    if (!currentBusiness?.brandId) return;
    setRosterLoading(true);
    setRosterError(false);
    try {
      const response = await makeAuthenticatedRequest(`/api/v1/staff/business/${currentBusiness.brandId}`);
      if (response.ok) {
        const data = await response.json();
        setStaffRoster(Array.isArray(data?.staff) ? data.staff : []);
      } else {
        // 403/500 → show an honest error state, NOT a false "No staff in this business yet".
        setRosterError(true);
        console.error('Staff roster fetch failed:', response.status);
      }
    } catch (error) {
      setRosterError(true);
      console.error('Failed to fetch staff roster:', error);
    } finally {
      setRosterLoading(false);
    }
  };

  const fetchCallHistory = async () => {
    if (!currentBusiness?.brandId) return;
    
    setLoadingHistory(true);
    try {
      const response = await makeAuthenticatedRequest(`/api/v1/webrtc/call-logs/${currentBusiness.brandId}?limit=20&call_category=staff_to_staff`);
      if (response.ok) {
        const data = await response.json();
        setCallHistory(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch call history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Authoritative tenant-scoped call-stats. Returns true only on a real, applied refresh so the
  // caller can distinguish success from failure. On failure we DO NOT overwrite callStats with a
  // zero/mock — the previously-known real value stays until a genuine refresh replaces it, and the
  // last-refreshed timestamp (only bumped on success) tells the user how current the numbers are.
  const fetchCallStats = async () => {
    if (!currentBusiness?.brandId) return false;
    try {
      const response = await makeAuthenticatedRequest(`/api/v1/webrtc/call-stats/${currentBusiness.brandId}?period=30d`);
      if (response.ok) {
        const data = await response.json();
        setCallStats(data);
        return true;
      }
      console.error('Call stats fetch failed:', response.status);
      return false;
    } catch (error) {
      console.error('Failed to fetch call stats:', error);
      return false;
    }
  };

  // Header "Refresh Stats" — refreshes the AUTHORITATIVE tenant-scoped call statistics (and the
  // supporting displayed data: history + live presence). Contract:
  //  • single-flight: a concurrent click while a refresh is in flight is ignored (statsRefreshing guard);
  //  • loading: the button shows a spinner + disables while running;
  //  • updates: the stat cards (total/successful/success-rate), analytics, history and online count
  //    all reflect the just-fetched values; a last-refreshed timestamp is recorded on success;
  //  • safe errors: a failure shows a non-destructive toast and does NOT bump the timestamp, so the
  //    displayed numbers are never misrepresented as freshly-current;
  //  • tenant-scoped: every request is keyed by currentBusiness.brandId (no cross-tenant leakage);
  //  • never mock/stale-as-truth: values only change from a real successful fetch.
  const handleRefreshStats = async () => {
    if (statsRefreshing) return;                       // duplicate concurrent request suppression
    if (!currentBusiness?.brandId) {
      toast({ title: 'No business selected', description: 'Cannot refresh statistics yet.', variant: 'destructive' });
      return;
    }
    setStatsRefreshing(true);
    try {
      const statsOk = await fetchCallStats();          // authoritative /webrtc/call-stats/{brandId}
      await Promise.all([
        fetchCallHistory(),
        Promise.resolve(typeof fetchOnlineUsers === 'function' ? fetchOnlineUsers() : null),
      ]);
      if (!statsOk) throw new Error('stats_unavailable');
      setLastStatsRefresh(new Date());                 // only on a genuine success
    } catch (e) {
      toast({ title: 'Refresh failed', description: 'Statistics could not be refreshed. Please try again.', variant: 'destructive' });
    } finally {
      setStatsRefreshing(false);
    }
  };

  // Staff-chat recipient visibility: poll the caller's own staff-chat conversations and build a
  // per-peer unread map so a new message shows a badge on the roster WITHOUT the recipient having
  // that peer's chat modal open. Reads the real /staff-chat/conversations (unread_count is computed
  // server-side, participant-scoped). No customer conversation data is touched.
  const fetchStaffConversations = async () => {
    if (!currentBusiness?.brandId) return;
    const self = auth?.currentUser?.uid || user?.uid || user?.id || null;
    try {
      const resp = await makeAuthenticatedRequest(
        `/api/v1/staff-chat/conversations?business_id=${encodeURIComponent(currentBusiness.brandId)}`
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const map = {};
      (data.conversations || []).forEach((c) => {
        const n = c.unread_count || 0;
        if (!n) return;
        // The other participant keys the roster row. `participants` are firebase_uids (or the peer's
        // staff id when they have never logged in) — either way it matches teamMembers' member.id.
        const peerUid = (c.participants || []).find((p) => p && p !== self);
        const peerStaff = (c.participant_staff_ids || []).find((s) => s);
        if (peerUid) map[peerUid] = (map[peerUid] || 0) + n;
        // Also index by the peer staff id as a fallback key (offline staff rows use s.id).
        if (peerStaff && peerStaff !== peerUid) map[peerStaff] = Math.max(map[peerStaff] || 0, n);
      });
      setStaffUnread(map);
    } catch (_) { /* poll — ignore transient errors */ }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const formatTimestamp = (timestamp) => {
    return formatInTenantTz(timestamp);
  };

  // Quick Call dialpad — staff-to-staff WebRTC ONLY. We resolve the entered number to a real
  // tenant staff member by phone and ring them via WebRTC. External/PSTN dialing is not supported
  // (no backend), so we say so honestly instead of pretending to place a call.
  const digitsOnly = (s) => (s || '').replace(/\D/g, '').replace(/^0+/, '');
  const handleQuickCall = () => {
    const raw = (phoneNumber || '').trim();
    if (!raw) {
      toast({ title: "Invalid Number", description: "Please enter a phone number.", variant: "destructive" });
      return;
    }
    const target = digitsOnly(raw);
    const match = (staffRoster || []).find(
      (s) => s.phone && target.length >= 7 && digitsOnly(s.phone).endsWith(target)
    );
    if (!match) {
      toast({
        title: "No tenant staff found",
        description: "No tenant staff found for this number. External (PSTN) dialing is not supported in this release.",
        variant: "destructive",
      });
      return;
    }
    const id = match.firebase_uid || match.id;
    if (id === selfUid) {
      toast({ title: "Cannot call yourself", description: "Pick another staff member.", variant: "destructive" });
      return;
    }
    setQuickCallOpen(false);
    setPhoneNumber('');
    initiateCall(id, 'voice');
  };

  const dialpadNumbers = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#']
  ];

  const handleDialpadClick = (number) => {
    setPhoneNumber(prev => prev + number);
  };

  const clearPhoneNumber = () => {
    setPhoneNumber('');
  };

  // Presence = real tenant staff roster JOINED with live WebRTC online users. No fake/static data.
  const selfUid = auth?.currentUser?.uid || user?.uid || user?.id || null;
  const onlineSet = new Set((onlineUsers || []).filter(Boolean));
  // Team Members = ALL real staff (online AND offline) with presence + self label. Offline staff are kept.
  const teamMembers = (staffRoster || [])
    .map((s) => {
      const id = s.firebase_uid || s.id || s.uid;
      if (!id) return null;
      return {
        id,                                   // signaling/presence id (firebase uid) — call target
        staffId: s.id,
        name: s.name || s.full_name || 'Staff',
        role: s.role || '',
        status: onlineSet.has(id) ? 'online' : 'offline',
        isSelf: !!selfUid && id === selfUid,
      };
    })
    .filter(Boolean);
  // Quick Call contacts = only genuinely-online staff, excluding self.
  const realContacts = teamMembers.filter((m) => m.status === 'online' && !m.isSelf);

  // Resolve a call participant uid (firebase uid; may carry "__session") → real staff name/role.
  const resolveStaff = (uid) => {
    const base = (uid || '').split('__')[0];
    const m = teamMembers.find((t) => t.id === base);
    return m ? { name: m.name, role: m.role } : null;
  };

  // Conversation initiation
  const handleInitiateConversation = async (user) => {
    // Staff-to-staff chat (Team Members chat icon) → real staff DM, NEVER the customer ticket flow.
    if (user?.staff) {
      setStaffChatPeer({ id: user.id, name: user.name, role: user.role });
      return;
    }
    if (user.name === 'Team Member') {
      // Open team conversation dialog
      setTeamConversationOpen(true);
    } else {
      // Handle individual customer conversation using existing Tickets/Conversations system
      try {
        toast({
          title: "Starting Conversation",
          description: `Initiating chat with ${user.name}...`,
        });
        
        // Store user data for Tickets/Conversations system to pick up.
        // Use only the real identity fields on the object — never synthesize a
        // placeholder email/phone. Missing values stay empty (honest).
        sessionStorage.setItem('newConversationCustomer', JSON.stringify({
          id: user.id || `customer_${Date.now()}`,
          name: user.name,
          email: user.email || '',
          phone: user.phone || user.number || '',
          company: user.company || ''
        }));
        
        // Navigate to Tickets/Conversations system
        setTimeout(() => {
          window.location.href = '/business/tickets';
        }, 500);
        
      } catch (error) {
        console.error('Error starting conversation:', error);
        toast({
          title: "Failed to Start Conversation",
          description: "Unable to initiate conversation. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleStartTeamConversation = async () => {
    console.log('VoiceCalls: Starting team conversation...', {
      selectedTeamMembers,
      conversationMessage: conversationMessage.substring(0, 50),
      user,
      currentBusiness
    });

    if (selectedTeamMembers.length === 0) {
      toast({
        title: "No Team Members Selected",
        description: "Please select at least one team member to start the conversation.",
        variant: "destructive",
      });
      return;
    }

    if (!conversationMessage.trim()) {
      toast({
        title: "Message Required",
        description: "Please enter a message to start the team conversation.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create a group conversation with API call
      const backendUrl = process.env.REACT_APP_API_BASE;
      console.log('VoiceCalls: Using backend URL:', backendUrl);
      
      const conversationData = {
        participants: selectedTeamMembers,
        initiator: user?.id || 'voice_call_user',
        business_id: currentBusiness?.brandId || user?.brandId || 'voice_call_business',
        type: 'team_conversation',
        initial_message: conversationMessage,
        created_at: new Date().toISOString()
      };

      console.log('VoiceCalls: Sending conversation data:', conversationData);

      const response = await makeAuthenticatedRequest(`/api/v1/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(conversationData)
      });

      console.log('VoiceCalls: API Response:', {
        status: response.status,
        ok: response.ok
      });

      if (response.ok) {
        const result = await response.json();
        console.log('VoiceCalls: Conversation created successfully:', result);
        
        const memberNames = selectedTeamMembers.map(id => {
          const member = teamMembers.find(contact => contact.id === id);
          return member ? member.name : `Member ${id}`;
        }).join(', ');

        toast({
          title: "Team Conversation Started",
          description: `Group conversation initiated with: ${memberNames}. Conversation ID: ${result.id}`,
        });

        // Reset form and close dialog
        setSelectedTeamMembers([]);
        setConversationMessage('');
        setTeamConversationOpen(false);

        // Optionally redirect to conversations page or open the conversation
        // window.location.href = `/conversations/${result.id}`;
        
      } else {
        const errorText = await response.text();
        console.error('VoiceCalls: API Error Response:', errorText);
        throw new Error(`Failed to create conversation: ${response.status} - ${errorText}`);
      }
      
    } catch (error) {
      console.error('VoiceCalls: Error starting team conversation:', error);
      
      // Fallback: At least create a local notification and log
      const memberNames = selectedTeamMembers.map(id => {
        const member = realContacts.find(contact => contact.id === id);
        return member ? member.name : `Member ${id}`;
      }).join(', ');

      toast({
        title: "Conversation Initiated Locally", 
        description: `Group conversation request sent to: ${memberNames}. Backend integration pending - ${error.message}`,
        variant: "default",
      });

      // Reset form and close dialog
      setSelectedTeamMembers([]);
      setConversationMessage('');
      setTeamConversationOpen(false);
    }
  };

  const handleTeamMemberToggle = (memberId) => {
    setSelectedTeamMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // M17 — open the CUSTOMER support-ticket modal (customer + conversation are chosen inside the
  // modal; the team-member context is intentionally ignored — a support ticket is for a customer).
  const openCustomerTicketModal = () => {
    setTicketData({ title: '', description: '', priority: 'medium', category: 'general' });
    setSelTicketCustomerId('');
    setSelTicketConversationId('');
    setTicketConversations([]);
    setTicketIdemKey('');
    setCreateTicketOpen(true);
    loadTicketCustomers();
  };

  const loadTicketCustomers = async () => {
    const bid = currentBusiness?.brandId;
    if (!bid) return;
    setTicketCustomersLoading(true);
    try {
      const r = await makeAuthenticatedRequest(`/api/v1/customers/business/${bid}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setTicketCustomers((d.customers || d.data || (Array.isArray(d) ? d : [])).filter(Boolean));
    } catch (e) {
      toast({ title: 'Failed to load customers', description: e.message, variant: 'destructive' });
      setTicketCustomers([]);
    } finally {
      setTicketCustomersLoading(false);
    }
  };

  // Load ONLY the selected customer's conversations (no customer-first fallback elsewhere).
  const onSelectTicketCustomer = async (custId) => {
    setSelTicketCustomerId(custId);
    setSelTicketConversationId('');
    setTicketConversations([]);
    if (!custId) return;
    setTicketConvLoading(true);
    try {
      const r = await makeAuthenticatedRequest(`/api/v1/conversations/customer/${encodeURIComponent(custId)}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setTicketConversations((d.conversations || d.data || (Array.isArray(d) ? d : [])).filter(Boolean));
    } catch (e) {
      toast({ title: 'Failed to load conversations', description: e.message, variant: 'destructive' });
      setTicketConversations([]);
    } finally {
      setTicketConvLoading(false);
    }
  };

  const handleCreateCustomerTicket = async () => {
    if (!selTicketCustomerId) { toast({ title: 'Select a customer', variant: 'destructive' }); return; }
    if (!selTicketConversationId) { toast({ title: 'Select a conversation', variant: 'destructive' }); return; }
    if (!ticketData.title.trim() || !ticketData.description.trim()) {
      toast({ title: 'Missing Information', description: 'Please provide both title and description.', variant: 'destructive' });
      return;
    }
    if (ticketSubmitting) return;  // disable duplicate submission while pending -> exactly one POST
    // One UUID per deliberate submission; reuse the same key only for retries (kept until success).
    let key = ticketIdemKey;
    if (!key) {
      key = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setTicketIdemKey(key);
    }
    setTicketSubmitting(true);
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/tickets/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_id: currentBusiness?.brandId,
          customer_id: selTicketCustomerId,
          conversation_id: selTicketConversationId,
          title: ticketData.title,
          description: ticketData.description,
          priority: ticketData.priority,
          category: ticketData.category,
          idempotency_key: key,
        }),
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      toast({ title: 'Support Ticket Created', description: `Ticket ${data.ticket_id} created.` });
      // Reset ONLY after confirmed success.
      setTicketData({ title: '', description: '', priority: 'medium', category: 'general' });
      setSelTicketCustomerId('');
      setSelTicketConversationId('');
      setTicketConversations([]);
      setTicketIdemKey('');
      setCreateTicketOpen(false);
    } catch (error) {
      // Keep the modal open and the SAME idempotency key so a retry is safe.
      toast({ title: 'Error Creating Ticket', description: error.message, variant: 'destructive' });
    } finally {
      setTicketSubmitting(false);
    }
  };

  // Start a staff AUDIO conference (mesh). Host can start solo and add teammates live
  // from the conference panel; online teammates are auto-invited on start.
  const handleStartConference = async () => {
    const invitees = (realContacts || []).map((c) => c.id).filter(Boolean);
    const ok = await startConference(invitees);
    if (ok) {
      toast({
        title: 'Audio Conference started',
        description: invitees.length
          ? `Inviting ${invitees.length} online teammate(s)…`
          : 'Conference ready — add teammates from the panel as they come online.',
      });
    } else {
      toast({
        title: 'Could not start conference',
        description: confError || 'Please try again.',
        variant: 'destructive',
      });
    }
  };


  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* WebRTC Components */}
      {/* Incoming-call ring + active-call UI are rendered GLOBALLY by WebRTCProvider
          (single shared instance). VoiceCalls no longer renders its own to avoid duplicates. */}

      {/* Staff-to-staff direct chat (Phase A) — real, persisted, tenant-scoped. NOT tickets. */}
      <StaffChatModal
        open={!!staffChatPeer}
        peer={staffChatPeer}
        businessId={currentBusiness?.brandId}
        selfUid={selfUid}
        makeAuthenticatedRequest={makeAuthenticatedRequest}
        onClose={() => { setStaffChatPeer(null); fetchStaffConversations(); }}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <User className="h-6 w-6 text-purple-600" />
              {t('page.voiceCalls.title')}
            </h1>
            <p className="text-gray-600 flex items-center gap-1 mt-1">
              {t('page.voiceCalls.subtitle')}
            </p>
          </div>
          <div className="flex space-x-3">
            <div className="flex flex-col items-start">
              <Button variant="outline" onClick={handleRefreshStats} disabled={statsRefreshing}>
                <TrendingUp className={`h-4 w-4 mr-2 ${statsRefreshing ? 'animate-spin' : ''}`} />
                {statsRefreshing ? t('tc.refreshing') : t('common.refreshStats')}
              </Button>
              {lastStatsRefresh && (
                <span className="text-[11px] text-gray-400 mt-1">
                  {t('tc.lastRefreshed')}: {formatTimestamp(lastStatsRefresh.toISOString())}
                </span>
              )}
            </div>
            <Dialog open={quickCallOpen} onOpenChange={setQuickCallOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                  <Phone className="h-4 w-4 mr-2" />
                  {t('common.quickCall')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('common.quickCall')}</DialogTitle>
                  <DialogDescription>
                    {t('tc.quickCall.desc')}
                  </DialogDescription>
                </DialogHeader>
                <Tabs defaultValue="dialpad" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="dialpad">
                      <Hash className="h-3 w-3 mr-1" />
                      {t('tc.tab.dialpad')}
                    </TabsTrigger>
                    <TabsTrigger value="recent">
                      <History className="h-3 w-3 mr-1" />
                      {t('tc.tab.recent')}
                    </TabsTrigger>
                    <TabsTrigger value="contacts">
                      <UserPlus className="h-3 w-3 mr-1" />
                      {t('tc.tab.contacts')}
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Dialpad Tab */}
                  <TabsContent value="dialpad" className="space-y-4">
                    <div className="text-center">
                      <Input
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder={t('tc.dialpad.phonePlaceholder')}
                        className="text-lg text-center mb-1"
                      />
                      <p className="text-xs text-gray-500 mb-4">
                        {t('tc.dialpad.help')}
                      </p>
                      <div className="grid grid-cols-3 gap-2 max-w-48 mx-auto">
                        {dialpadNumbers.flat().map((number) => (
                          <Button
                            key={number}
                            variant="outline"
                            className="h-12 text-lg"
                            onClick={() => handleDialpadClick(number)}
                          >
                            {number}
                          </Button>
                        ))}
                      </div>
                      <div className="flex space-x-2 mt-4">
                        <Button variant="outline" onClick={clearPhoneNumber} className="flex-1">
                          {t('tc.clear')}
                        </Button>
                        <Button onClick={handleQuickCall} className="flex-1">
                          <Phone className="h-4 w-4 mr-2" />
                          {t('tc.call')}
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                  
                  {/* Recent Calls Tab — REAL staff-to-staff call_logs (no fake data). Honest empty when none. */}
                  <TabsContent value="recent" className="space-y-2">
                    {callHistory.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Clock className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                        <p>{t('tc.noRecentCalls')}</p>
                      </div>
                    ) : (
                      callHistory.map((call) => {
                        const otherUid = call.caller_uid === selfUid ? call.callee_uid : call.caller_uid;
                        const other = resolveStaff(otherUid);
                        const name = displayStaffName(other?.name); // D3: resolved staff name or 'Staff member', never a uid fragment
                        const outgoing = call.caller_uid === selfUid;
                        const status = call.status || '';
                        const failedish = status === 'missed' || status === 'rejected' || status === 'failed';
                        return (
                          <div key={`recent-${call.call_id}`} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                            <div className="flex items-center space-x-3">
                              <Avatar className="h-8 w-8"><AvatarFallback>{(name || '?')[0]}</AvatarFallback></Avatar>
                              <div>
                                <div className="font-medium text-sm">{name}{other?.role ? ` · ${other.role}` : ''}</div>
                                <div className="text-xs text-gray-500 flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {outgoing ? 'Outgoing' : 'Incoming'} · {call.call_type || 'voice'} · {status}
                                  {call.duration_seconds != null ? ` · ${call.duration_seconds}s` : ''}
                                </div>
                              </div>
                            </div>
                            {failedish && <PhoneOff className="h-4 w-4 text-red-500" />}
                          </div>
                        );
                      })
                    )}
                  </TabsContent>
                  
                  {/* Contacts Tab */}
                  <TabsContent value="contacts" className="space-y-2">
                    {realContacts.length === 0 && (
                      <div className="text-sm text-gray-500 text-center py-6">{t('tc.noOtherStaffOnline')}</div>
                    )}
                    {realContacts.map((contact) => (
                      <div
                        key={`contact-${contact.id}`}
                        className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer ${selectedContact?.id === contact.id ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}
                        onClick={() => setSelectedContact(contact)}
                      >
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{contact.name[0]}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-sm">{contact.name}</div>
                            <div className="text-xs text-gray-500">{contact.role}</div>
                          </div>
                          <Badge variant={contact.status === 'online' ? 'default' : 'secondary'} className="text-xs">
                            {contact.status}
                          </Badge>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCustomerTicketModal();  // M17 — customer ticket (pick customer+conversation in modal)
                            }}
                            title={t('tc.createTicket.title')}
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                          {/* Staff WebRTC call by IDENTITY (firebase_uid) — NOT the dialpad phone path. */}
                          <Button size="sm" title={t('tc.voiceCall')} onClick={async (e) => {
                            e.stopPropagation();
                            setQuickCallOpen(false);
                            try { await initiateCall(contact.id, 'voice'); }
                            catch (err) { toast({ title: 'Call failed', description: 'Could not start the call.', variant: 'destructive' }); }
                          }}>
                            <Phone className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" title={t('tc.videoCall')} onClick={async (e) => {
                            e.stopPropagation();
                            setQuickCallOpen(false);
                            try { await initiateCall(contact.id, 'video'); }
                            catch (err) { toast({ title: 'Call failed', description: 'Could not start the call.', variant: 'destructive' }); }
                          }}>
                            <Video className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </TabsContent>
                </Tabs>

                {/* Selected Contact Details */}
                {selectedContact && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-800">{t('tc.selectedLabel', { name: selectedContact.name })}</p>
                    <p className="text-xs text-blue-600">{selectedContact.number || selectedContact.phone}</p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Call Statistics */}
        {callStats && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-4 text-center">
                <PhoneCall className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <div className="text-lg font-bold text-gray-900">{callStats.total_calls}</div>
                <div className="text-xs text-gray-600">{t('tc.stat.totalCalls')}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-4 text-center">
                <Activity className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <div className="text-lg font-bold text-green-700">{callStats.successful_calls}</div>
                <div className="text-xs text-gray-600">{t('tc.stat.successful')}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-4 text-center">
                <BarChart3 className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <div className="text-lg font-bold text-purple-700">{callStats.success_rate}%</div>
                <div className="text-xs text-gray-600">{t('tc.stat.successRate')}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                <div className="text-lg font-bold text-orange-700">{onlineUsers.length}</div>
                <div className="text-xs text-gray-600">{t('tc.stat.onlineNow')}</div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Tabs defaultValue="active" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="active">{t('tc.tab.activeComm')}</TabsTrigger>
          <TabsTrigger value="history">
            <History className="h-4 w-4 mr-1" />
            {callHistoryLabel}
          </TabsTrigger>
          <TabsTrigger value="analytics">{t('tc.tab.analytics')}</TabsTrigger>
        </TabsList>

        {/* Active Communication Tab */}
        <TabsContent value="active" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <OnlineUsersPanel
              teamMembers={teamMembers}
              isConnected={isConnected}
              onInitiateCall={initiateCall}
              currentCall={currentCall}
              onRefresh={() => { fetchStaffRoster(); fetchOnlineUsers(); }}
              onInitiateConversation={handleInitiateConversation}
              unreadByPeer={staffUnread}
              isLoading={rosterLoading}
              error={rosterError}
            />
            
            {/* Quick Actions */}
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('tc.quickActions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Customer interaction buttons */}
                  <Button 
                    className="w-full justify-start bg-blue-50 text-blue-700 hover:bg-blue-100"
                    onClick={() => handleStartCustomerCall('voice')}
                  >
                    <Phone className="h-4 w-4 mr-3" />
                    {t('tc.startVoiceCall')}
                  </Button>
                  
                  <Button 
                    className="w-full justify-start bg-green-50 text-green-700 hover:bg-green-100"
                    onClick={() => handleStartCustomerCall('video')}
                  >
                    <Video className="h-4 w-4 mr-3" />
                    {t('tc.startVideoCall')}
                  </Button>
                  
                  <Button 
                    className="w-full justify-start bg-purple-50 text-purple-700 hover:bg-purple-100"
                    onClick={() => handleStartCustomerConversation()}
                  >
                    <MessageSquare className="h-4 w-4 mr-3" />
                    {t('tc.startCustomerConversation')}
                  </Button>

                  {/* Staff AUDIO conference (mesh). Host can start solo and add teammates live. */}
                  <div className="flex flex-col">
                    <Button
                      onClick={handleStartConference}
                      disabled={!!conference}
                      className="w-full justify-start bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-60"
                      title={conference ? t('tc.conf.inProgressTitle') : t('tc.conf.startTitle')}
                    >
                      <Mic className="h-4 w-4 mr-3" />
                      {conference ? t('tc.conf.inProgress') : t('tc.startAudioConference')}
                    </Button>
                    <span className="text-xs text-gray-500 mt-1">
                      {conference ? t('tc.conf.manageHint') : t('tc.conf.audioOnlyHint')}
                    </span>
                  </div>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    disabled={rosterLoading}
                    onClick={async () => {
                      toast({ title: 'Refreshing…', description: 'Reloading stats, logs, roster and presence.' });
                      try {
                        await Promise.all([fetchCallStats(), fetchStaffRoster(), fetchOnlineUsers(), fetchCallHistory()]);
                        toast({ title: 'Refreshed', description: 'Voice Calls data updated.' });
                      } catch (e) {
                        toast({ title: 'Refresh failed', description: 'Some data could not be reloaded.', variant: 'destructive' });
                      }
                    }}
                  >
                    <TrendingUp className="h-4 w-4 mr-3" />
                    {rosterLoading ? t('tc.refreshing') : t('tc.refreshStatistics')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Call History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{callHistoryLabel}</CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                {isCallHistorySupervisor
                  ? t('tc.history.supervisorDesc')
                  : t('tc.history.ownDesc')}
              </p>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">{t('tc.history.loading')}</p>
                </div>
              ) : callHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>{t('tc.history.empty')}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('tc.history.emptyHint')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {callHistory.map((call, index) => (
                    <div key={`call-${call.id || index}`} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          {call.status === 'missed' || call.status === 'failed' ? (
                            <PhoneOff className="h-5 w-5 text-white" />
                          ) : call.call_type === 'video' ? (
                            <Video className="h-5 w-5 text-white" />
                          ) : (
                            <Phone className="h-5 w-5 text-white" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {call.caller_id} → {call.callee_id}
                          </h4>
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTimestamp(call.start_time)} · {call.call_type}
                          </p>
                        </div>
                      </div>
                      
                      {/* Recording Play/Download controls removed: WebRTC staff-to-staff calls are
                          peer-to-peer, nothing is captured/stored, and no recording backend exists.
                          Real recording is a deferred Phase 6 item (capture/consent/retention/
                          storage/RBAC/audit). Call history below stays intact (status + duration). */}
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <Badge className={
                            call.status === 'answer' ? 'bg-green-100 text-green-800' :
                            call.status === 'offer' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {call.status}
                          </Badge>
                          {call.duration_seconds && (
                            <p className="text-sm text-gray-500 mt-1">
                              {formatDuration(call.duration_seconds)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('tc.callPerformance')}</CardTitle>
              </CardHeader>
              <CardContent>
                {callStats ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{t('tc.stat.successRate')}</span>
                      <span className="text-lg font-bold text-green-600">{callStats.success_rate}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{t('tc.stat.totalCalls')}</span>
                      <span className="text-lg font-bold text-blue-600">{callStats.total_calls}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{t('tc.stat.completedCalls')}</span>
                      <span className="text-lg font-bold text-purple-600">{callStats.completed_calls}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>{t('tc.analytics.empty')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('tc.connectionStatus')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('tc.webrtcStatus')}</span>
                    <Badge className={isConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {isConnected ? 'Connected' : 'Disconnected'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('tc.onlineUsers')}</span>
                    <span className="text-lg font-bold text-blue-600">{onlineUsers.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('tc.currentCall')}</span>
                    <Badge className={currentCall ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                      {currentCall ? 'Active' : 'None'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Team Conversation Dialog */}
      <Dialog open={teamConversationOpen} onOpenChange={setTeamConversationOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('tc.teamConv.title')}</DialogTitle>
            <DialogDescription>
              {t('tc.teamConv.desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Team Member Selection */}
            <div>
              <Label className="text-base font-medium">{t('tc.teamConv.selectMembers')}</Label>
              <div className="mt-3 space-y-3 max-h-48 overflow-y-auto border rounded-lg p-3">
                {teamMembers.length === 0 && (
                  <div className="text-sm text-gray-500 text-center py-2">{t('tc.noStaffYet')}</div>
                )}
                {teamMembers.map((member) => (
                  <div key={member.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={`member-${member.id}`}
                      checked={selectedTeamMembers.includes(member.id)}
                      onCheckedChange={() => handleTeamMemberToggle(member.id)}
                      disabled={member.isSelf}
                    />
                    <Label
                      htmlFor={`member-${member.id}`}
                      className="flex items-center space-x-3 cursor-pointer flex-1"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          member.status === 'online' ? 'bg-green-500' :
                          member.status === 'busy' ? 'bg-yellow-500' : 'bg-gray-400'
                        }`} />
                        <div>
                          <div className="font-medium">{member.name}{member.isSelf ? ` ${t('tc.you')}` : ''}</div>
                          <div className="text-sm text-gray-500">{member.role}{member.status === 'offline' ? ' · offline' : ''}</div>
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
              {selectedTeamMembers.length > 0 && (
                <div className="mt-2 text-sm text-blue-600">
                  {t('tc.teamConv.membersSelected', { count: selectedTeamMembers.length })}
                </div>
              )}
            </div>

            {/* Message Composition */}
            <div>
              <Label htmlFor="team-message" className="text-base font-medium">
                {t('tc.teamConv.message')}
              </Label>
              <Textarea
                id="team-message"
                placeholder={t('tc.teamConv.messagePlaceholder')}
                value={conversationMessage}
                onChange={(e) => setConversationMessage(e.target.value)}
                className="mt-2"
                rows={4}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setTeamConversationOpen(false);
                  setSelectedTeamMembers([]);
                  setConversationMessage('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleStartTeamConversation}
                className="bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {t('tc.startConversation')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={createTicketOpen} onOpenChange={setCreateTicketOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('tc.createTicket.title')}</DialogTitle>
            <DialogDescription>
              {t('tc.createTicket.desc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="ticket-customer">{t('tc.createTicket.customerReq')}</Label>
              <select
                id="ticket-customer"
                value={selTicketCustomerId}
                onChange={(e) => onSelectTicketCustomer(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                disabled={ticketCustomersLoading}
              >
                <option value="">{ticketCustomersLoading ? t('tc.createTicket.loadingCustomers') : t('tc.createTicket.selectCustomer')}</option>
                {ticketCustomers.map((c) => {
                  const cid = c.customer_id || c.id;
                  return <option key={cid} value={cid}>{c.name || c.full_name || cid}</option>;
                })}
              </select>
            </div>

            <div>
              <Label htmlFor="ticket-conversation">{t('tc.createTicket.conversationReq')}</Label>
              <select
                id="ticket-conversation"
                value={selTicketConversationId}
                onChange={(e) => setSelTicketConversationId(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                disabled={!selTicketCustomerId || ticketConvLoading}
              >
                <option value="">
                  {!selTicketCustomerId ? t('tc.createTicket.selectCustomerFirst')
                    : ticketConvLoading ? t('tc.createTicket.loadingConversations')
                    : ticketConversations.length === 0 ? t('tc.createTicket.noConversations')
                    : t('tc.createTicket.selectConversation')}
                </option>
                {ticketConversations.map((cv) => {
                  const id = cv.id || cv.conversation_id;
                  const label = `${String(id).slice(0, 8)} · ${cv.status || cv.category || 'conversation'}`;
                  return <option key={id} value={id}>{label}</option>;
                })}
              </select>
            </div>

            <div>
              <Label htmlFor="ticket-title">{t('tc.createTicket.titleReq')}</Label>
              <Input
                id="ticket-title"
                value={ticketData.title}
                onChange={(e) => setTicketData(prev => ({...prev, title: e.target.value}))}
                placeholder={t('tc.createTicket.titlePlaceholder')}
              />
            </div>

            <div>
              <Label htmlFor="ticket-description">{t('tc.createTicket.descriptionReq')}</Label>
              <Textarea
                id="ticket-description"
                value={ticketData.description}
                onChange={(e) => setTicketData(prev => ({...prev, description: e.target.value}))}
                placeholder={t('tc.createTicket.descriptionPlaceholder')}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ticket-priority">{t('tc.createTicket.priority')}</Label>
                <select
                  id="ticket-priority"
                  value={ticketData.priority}
                  onChange={(e) => setTicketData(prev => ({...prev, priority: e.target.value}))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="low">{t('tc.priority.low')}</option>
                  <option value="medium">{t('tc.priority.medium')}</option>
                  <option value="high">{t('tc.priority.high')}</option>
                  <option value="urgent">{t('tc.priority.urgent')}</option>
                </select>
              </div>

              <div>
                <Label htmlFor="ticket-category">{t('tc.createTicket.category')}</Label>
                <select
                  id="ticket-category"
                  value={ticketData.category}
                  onChange={(e) => setTicketData(prev => ({...prev, category: e.target.value}))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="general">{t('tc.category.general')}</option>
                  <option value="technical">{t('tc.category.technical')}</option>
                  <option value="billing">{t('tc.category.billing')}</option>
                  <option value="feature">{t('tc.category.feature')}</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setCreateTicketOpen(false)} disabled={ticketSubmitting}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleCreateCustomerTicket}
                disabled={ticketSubmitting || !selTicketCustomerId || !selTicketConversationId || !ticketData.title.trim() || !ticketData.description.trim()}
              >
                {ticketSubmitting ? t('tc.createTicket.creating') : t('tc.createTicket.create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Conversation Dialog */}
      <Dialog open={quickConversationOpen} onOpenChange={setQuickConversationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tc.newConv.title')}</DialogTitle>
            <DialogDescription>
              {t('tc.newConv.desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="contact-name">{t('tc.newConv.nameReq')}</Label>
              <Input
                id="contact-name"
                value={conversationContactName}
                onChange={(e) => setConversationContactName(e.target.value)}
                placeholder={t('tc.newConv.namePlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="contact-phone">{t('tc.newConv.phoneOptional')}</Label>
              <Input
                id="contact-phone"
                value={conversationContactPhone}
                onChange={(e) => setConversationContactPhone(e.target.value)}
                placeholder="+1234567890"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setQuickConversationOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleInitiateQuickConversation}>
                <MessageSquare className="h-4 w-4 mr-2" />
                {t('tc.newConv.startChat')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoiceCalls;